<template>
  <div class="timer" :class="{ 'timer-running': isRunning, 'timer-paused': isPaused }">
    <div class="timer-label">{{ label }}</div>
    <div class="timer-value">{{ formattedTime }}</div>
    <div class="timer-controls" v-if="showControls">
      <button v-if="!isRunning && !isPaused" class="timer-btn start-btn" @click="start">
        <i class="fas fa-play"></i>
      </button>
      <button v-if="isRunning && !isPaused" class="timer-btn pause-btn" @click="pause">
        <i class="fas fa-pause"></i>
      </button>
      <button v-if="isPaused" class="timer-btn resume-btn" @click="resume">
        <i class="fas fa-play"></i>
      </button>
      <button v-if="isRunning || isPaused" class="timer-btn reset-btn" @click="reset">
        <i class="fas fa-redo-alt"></i>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Timer',
  props: {
    label: {
      type: String,
      default: '计时'
    },
    initialSeconds: {
      type: Number,
      default: 0
    },
    autoStart: {
      type: Boolean,
      default: false
    },
    showControls: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      seconds: this.initialSeconds,
      isRunning: false,
      isPaused: false,
      interval: null
    };
  },
  computed: {
    formattedTime() {
      const minutes = Math.floor(this.seconds / 60);
      const seconds = this.seconds % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
  },
  mounted() {
    if (this.autoStart) {
      this.start();
    }
  },
  beforeDestroy() {
    this.clearInterval();
  },
  methods: {
    start() {
      if (!this.isRunning) {
        this.isRunning = true;
        this.isPaused = false;
        this.startInterval();
        this.$emit('start');
      }
    },
    pause() {
      if (this.isRunning && !this.isPaused) {
        this.isPaused = true;
        this.clearInterval();
        this.$emit('pause', this.seconds);
      }
    },
    resume() {
      if (this.isPaused) {
        this.isPaused = false;
        this.startInterval();
        this.$emit('resume');
      }
    },
    reset() {
      this.clearInterval();
      this.seconds = 0;
      this.isRunning = false;
      this.isPaused = false;
      this.$emit('reset');
    },
    startInterval() {
      this.clearInterval(); // 清除之前的计时器，避免重复
      this.interval = setInterval(() => {
        this.seconds++;
        this.$emit('tick', this.seconds);
      }, 1000);
    },
    clearInterval() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    },
    setTime(seconds) {
      this.seconds = seconds;
    }
  }
}
</script>

<style scoped>
.timer {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem;
  border-radius: 8px;
  background-color: var(--card-bg, rgba(255, 255, 255, 0.7));
  border: 1px solid var(--border-color, rgba(203, 213, 225, 0.5));
}

.timer-running {
  border-color: var(--success-color, #2ecc71);
}

.timer-paused {
  border-color: var(--warning-color, #f39c12);
}

.timer-label {
  font-size: 0.875rem;
  color: var(--text-secondary, #64748b);
  margin-bottom: 0.25rem;
}

.timer-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color, #1f2937);
  font-family: monospace;
}

.timer-controls {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.timer-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s;
}

.start-btn, .resume-btn {
  background-color: var(--success-color, #2ecc71);
  color: white;
}

.pause-btn {
  background-color: var(--warning-color, #f39c12);
  color: white;
}

.reset-btn {
  background-color: var(--danger-color, #e74c3c);
  color: white;
}

.timer-btn:hover {
  opacity: 0.9;
}

/* 暗色主题支持 */
[data-theme="dark"] .timer {
  background-color: var(--card-bg, rgba(42, 42, 42, 0.8));
  border-color: var(--border-color, rgba(75, 85, 99, 0.5));
}

[data-theme="dark"] .timer-label {
  color: var(--text-secondary, #9ca3af);
}

[data-theme="dark"] .timer-value {
  color: var(--text-color, #e5e7eb);
}
</style>